/**
 * Per-route SEO. Titles follow `{Page} | {brand.shortName}` — the site name is
 * never "Welcome". JSON-LD Organization is emitted on every page, populated from
 * brand, and NewsArticle is emitted on press releases.
 *
 * Language: the active locale sets <html lang>. Translated corporate pages also
 * emit hreflang alternates against `?lang=`; English-only pages emit none, which
 * is the honest signal for a page that exists in one language.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import brand from '../brand/brand';
import { LOCALES, isTranslatedPath, localeFor } from '../i18n/locales';
import { useLocale } from '../i18n/LocaleProvider';

const ORIGIN = `https://${brand.domain}`;

export default function Seo({
  title,
  description,
  path = '/',
  noindex = false,
  ogImage = '/posters/hero-strait-field.webp',
  article,
}) {
  const { locale } = useLocale();
  const active = localeFor(locale);
  const translated = isTranslatedPath(path);
  const fullTitle = title ? `${title} | ${brand.shortName}` : brand.legalName;
  const suffix = translated && locale !== 'en' ? `?lang=${locale}` : '';
  const canonical = `${ORIGIN}${path}${suffix}`;

  const organisation = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: brand.legalName,
    legalName: brand.legalName,
    url: ORIGIN,
    logo: `${ORIGIN}${brand.icon}`,
    email: brand.irEmail,
    identifier: brand.coRegNo,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '51 Bukit Batok Crescent, #03-26/27 Unity Centre',
      postalCode: '658077',
      addressCountry: 'SG',
    },
    sameAs: ['https://www.supratechnic.com/', 'https://sii.asia/', 'https://suprayi.com/'],
  };

  const alternates = translated
    ? [
        ...LOCALES.map((entry) => (
          <link
            key={entry.code}
            rel="alternate"
            hrefLang={entry.hrefLang}
            href={`${ORIGIN}${path}${entry.code === 'en' ? '' : `?lang=${entry.code}`}`}
          />
        )),
        <link key="x-default" rel="alternate" hrefLang="x-default" href={`${ORIGIN}${path}`} />,
      ]
    : null;

  return (
    <Helmet prioritizeSeoTags>
      <html lang={active.htmlLang} />
      <title>{fullTitle}</title>
      {description ? <meta name="description" content={description} /> : null}
      <link rel="canonical" href={canonical} />
      {alternates}
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:site_name" content={brand.legalName} />
      <meta property="og:title" content={fullTitle} />
      {description ? <meta property="og:description" content={description} /> : null}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={`${ORIGIN}${ogImage}`} />
      <meta property="og:locale" content={active.htmlLang.replace('-', '_')} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description ? <meta name="twitter:description" content={description} /> : null}
      <meta name="twitter:image" content={`${ORIGIN}${ogImage}`} />
      <script type="application/ld+json">{JSON.stringify(organisation)}</script>
      {article ? (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: article.title,
            datePublished: article.published_at,
            url: canonical,
            publisher: { '@type': 'Organization', name: brand.legalName },
          })}
        </script>
      ) : null}
    </Helmet>
  );
}
